/**
 * Iter 251 — Objection Cookbook : librairie commerciale auto-apprenante par tenant.
 *
 * Chaque fois qu'un commercial utilise une suggestion du Coach IA pour traiter
 * une objection (detected_intent=objection/risk), l'entrée est automatiquement
 * sauvegardée et son compteur d'utilisation incrémenté.
 *
 * Cette page permet de :
 *  - Lister/filtrer les objections (favoris, intent, search)
 *  - Marquer en favori
 *  - Éditer la réponse
 *  - Ajouter manuellement des objections
 *  - Supprimer
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  BookOpenCheck, Star, Search, Plus, Trash2, Pencil, X, Loader2,
  Sparkles, Filter, Globe, Share2, Languages,
} from "lucide-react";
import { toast } from "sonner";

const INTENT_FILTERS = [
  { value: "", label: "Tous" },
  { value: "objection", label: "Objections" },
  { value: "risk", label: "Risques" },
];

const EXTRA_LANGS = [
  { code: "es", label: "🇪🇸 Espagnol" },
  { code: "de", label: "🇩🇪 Allemand" },
  { code: "it", label: "🇮🇹 Italien" },
  { code: "pt", label: "🇵🇹 Portugais" },
  { code: "nl", label: "🇳🇱 Néerlandais" },
  { code: "ar", label: "🇸🇦 Arabe" },
];

export default function ObjectionCookbookPage() {
  const { axiosAuth, user: currentUser } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const isManager = useMemo(() => {
    const role = (currentUser?.role || "").toLowerCase();
    return ["manager", "owner", "admin", "superadmin", "tenant_admin"].includes(role);
  }, [currentUser]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [intent, setIntent] = useState("");
  const [scope, setScope] = useState("team"); // "team" | "global"
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", agent_native: "", agent_translated: "", objection_intent: "objection" });
  // Iter 252 — on-demand translation cache per (obj_id, lang)
  const [translations, setTranslations] = useState({}); // { [obj_id]: { [lang]: text } }
  const [translating, setTranslating] = useState({}); // { [obj_id]: lang }

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (intent) params.set("intent", intent);
      let r;
      if (scope === "global") {
        r = await api.get(`/objection-cookbook/global?${params.toString()}`);
      } else {
        if (favoriteOnly) params.set("favorite", "true");
        r = await api.get(`/objection-cookbook?${params.toString()}`);
      }
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [favoriteOnly, intent, scope]);

  // Iter 252 — Toggle global sharing (manager only)
  const toggleShare = async (obj) => {
    try {
      const r = await api.patch(`/objection-cookbook/${obj.objection_id}/share`);
      setItems((prev) => prev.map((p) => p.objection_id === obj.objection_id ? { ...p, shared_globally: r.data.shared_globally } : p));
      toast.success(r.data.shared_globally ? "Partagé avec toutes les branches" : "Partage retiré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // Iter 252 — Translate on demand
  const translate = async (obj, lang) => {
    setTranslating((p) => ({ ...p, [obj.objection_id]: lang }));
    try {
      const r = await api.post(`/objection-cookbook/${obj.objection_id}/translate`, { target_lang: lang });
      setTranslations((p) => ({
        ...p,
        [obj.objection_id]: { ...(p[obj.objection_id] || {}), [lang]: r.data.translation },
      }));
      toast.success(r.data.cached ? "Traduction récupérée" : "Traduction générée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setTranslating((p) => ({ ...p, [obj.objection_id]: null }));
    }
  };

  const toggleFavorite = async (obj) => {
    try {
      await api.patch(`/objection-cookbook/${obj.objection_id}`, { ...obj, favorite: !obj.favorite });
      setItems((prev) => prev.map((p) => p.objection_id === obj.objection_id ? { ...p, favorite: !obj.favorite } : p));
    } catch (e) { toast.error("Erreur"); }
  };

  const remove = async (obj) => {
    if (!window.confirm(`Supprimer "${obj.label}" ?`)) return;
    try {
      await api.delete(`/objection-cookbook/${obj.objection_id}`);
      setItems((prev) => prev.filter((p) => p.objection_id !== obj.objection_id));
      toast.success("Supprimé");
    } catch (e) { toast.error("Erreur"); }
  };

  const startEdit = (obj) => {
    setEditing(obj.objection_id);
    setForm({
      label: obj.label,
      agent_native: obj.agent_native || "",
      agent_translated: obj.agent_translated || "",
      objection_intent: obj.objection_intent || "objection",
    });
  };

  const startCreate = () => {
    setEditing("new");
    setForm({ label: "", agent_native: "", agent_translated: "", objection_intent: "objection" });
  };

  const save = async () => {
    if (!form.label.trim()) { toast.error("Libellé requis"); return; }
    try {
      if (editing === "new") {
        await api.post("/objection-cookbook", form);
        toast.success("Entrée créée");
      } else {
        await api.patch(`/objection-cookbook/${editing}`, form);
        toast.success("Entrée mise à jour");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-6 max-w-[1300px] mx-auto space-y-5" data-testid="objection-cookbook-page">
      <PageHeader
        icon={<BookOpenCheck />}
        title="Cookbook d'objections"
        description="Librairie auto-apprenante : chaque fois que vous utilisez une suggestion du Coach IA, elle s'enregistre ici. Partagez les meilleures réponses avec votre équipe."
      />

      {/* Iter 252 — Scope tabs (Mon équipe vs Bibliothèque globale) */}
      <div className="flex items-center gap-2 border-b border-white/5" data-testid="cookbook-scope-tabs">
        <button
          onClick={() => setScope("team")}
          className={`px-3 py-2 text-sm font-bold border-b-2 -mb-px ${scope === "team" ? "text-cyan-400 border-cyan-400" : "text-slate-400 border-transparent hover:text-white"}`}
          data-testid="scope-team"
        >
          <BookOpenCheck className="w-3.5 h-3.5 inline mr-1.5" /> Mon équipe
        </button>
        <button
          onClick={() => setScope("global")}
          className={`px-3 py-2 text-sm font-bold border-b-2 -mb-px ${scope === "global" ? "text-purple-400 border-purple-400" : "text-slate-400 border-transparent hover:text-white"}`}
          data-testid="scope-global"
        >
          <Globe className="w-3.5 h-3.5 inline mr-1.5" /> Bibliothèque globale
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid="cookbook-toolbar">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Rechercher un libellé ou un texte…"
            className="w-full bg-[#0b0811] border border-white/10 rounded pl-7 pr-2 py-1.5 text-sm text-white"
            data-testid="cookbook-search"
          />
        </div>
        <select
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          className="bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-xs text-white"
          data-testid="cookbook-intent-filter"
        >
          {INTENT_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <Button
          onClick={() => setFavoriteOnly(!favoriteOnly)}
          variant={favoriteOnly ? "default" : "outline"}
          size="sm"
          className={favoriteOnly ? "bg-amber-500 hover:bg-amber-400 text-[#14101c]" : ""}
          disabled={scope === "global"}
          data-testid="cookbook-favorite-toggle"
        >
          <Star className={`w-3.5 h-3.5 mr-1.5 ${favoriteOnly ? "fill-current" : ""}`} /> Favoris
        </Button>
        <Button onClick={load} variant="outline" size="sm" data-testid="cookbook-search-go">
          <Filter className="w-3.5 h-3.5 mr-1.5" /> Filtrer
        </Button>
        {scope === "team" && (
          <Button onClick={startCreate} className="bg-purple-500 hover:bg-purple-400 text-white" size="sm" data-testid="cookbook-add">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter
          </Button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : items.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl py-12 text-center" data-testid="cookbook-empty">
          <BookOpenCheck className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="text-sm text-slate-400">
            {q || favoriteOnly || intent ? "Aucun résultat" : "Votre cookbook est vide pour l'instant"}
          </div>
          <div className="text-[11px] text-slate-600 mt-2 max-w-md mx-auto">
            Utilisez le Coach IA pendant vos appels traduits — vos meilleures réponses aux objections seront automatiquement ajoutées ici.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cookbook-list">
          {items.map((obj) => (
            <div key={obj.objection_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-2.5" data-testid={`cookbook-card-${obj.objection_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  {scope === "team" && (
                    <button
                      onClick={() => toggleFavorite(obj)}
                      className="shrink-0 mt-0.5 text-slate-500 hover:text-amber-400"
                      title={obj.favorite ? "Retirer des favoris" : "Marquer en favori"}
                      data-testid={`cookbook-favorite-${obj.objection_id}`}
                    >
                      <Star className={`w-4 h-4 ${obj.favorite ? "fill-amber-400 text-amber-400" : ""}`} />
                    </button>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white font-bold flex items-center gap-2 flex-wrap">
                      {obj.label}
                      {obj.shared_globally && scope === "team" && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/20">
                          <Globe className="w-2.5 h-2.5 inline mr-0.5" /> Partagé
                        </span>
                      )}
                      {scope === "global" && obj.source_tenant_label && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300">
                          {obj.source_tenant_label}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase mt-0.5">
                      {obj.objection_intent} · utilisé {obj.used_count} fois
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {scope === "team" && isManager && (
                    <button
                      onClick={() => toggleShare(obj)}
                      className={`p-1 ${obj.shared_globally ? "text-purple-400" : "text-slate-400 hover:text-purple-400"}`}
                      title={obj.shared_globally ? "Ne plus partager" : "Partager à toutes les branches"}
                      data-testid={`cookbook-share-${obj.objection_id}`}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {scope === "team" && (
                    <>
                      <button
                        onClick={() => startEdit(obj)}
                        className="p-1 text-slate-400 hover:text-white"
                        data-testid={`cookbook-edit-${obj.objection_id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(obj)}
                        className="p-1 text-slate-400 hover:text-rose-400"
                        data-testid={`cookbook-delete-${obj.objection_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {obj.agent_native && (
                <div className="text-xs text-slate-200 bg-white/[0.02] border border-white/5 rounded p-2">
                  <span className="text-[9px] uppercase text-slate-500 mr-1">FR</span>
                  {obj.agent_native}
                </div>
              )}
              {obj.agent_translated && (
                <div className="text-xs text-cyan-100 bg-cyan-500/5 border border-cyan-500/15 rounded p-2 italic">
                  <span className="text-[9px] uppercase text-cyan-400 mr-1">EN</span>
                  {obj.agent_translated}
                </div>
              )}
              {/* Iter 252 — Translations cache (visible after click) */}
              {translations[obj.objection_id] && Object.entries(translations[obj.objection_id]).map(([lang, txt]) => (
                <div key={lang} className="text-xs text-amber-100 bg-amber-500/5 border border-amber-500/15 rounded p-2 italic">
                  <span className="text-[9px] uppercase text-amber-400 mr-1">{lang.toUpperCase()}</span>
                  {txt}
                </div>
              ))}
              {/* Translate quick actions */}
              <div className="flex items-center gap-1 flex-wrap pt-1" data-testid={`cookbook-translate-${obj.objection_id}`}>
                <span className="text-[9px] text-slate-500 uppercase flex items-center gap-1">
                  <Languages className="w-2.5 h-2.5" /> Traduire vers :
                </span>
                {EXTRA_LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => translate(obj, l.code)}
                    disabled={translating[obj.objection_id] === l.code || (translations[obj.objection_id] && translations[obj.objection_id][l.code])}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-30"
                    data-testid={`translate-${obj.objection_id}-${l.code}`}
                  >
                    {translating[obj.objection_id] === l.code ? "…" : l.label}
                  </button>
                ))}
              </div>
              {obj.context_summary && (
                <div className="text-[10px] text-slate-500 line-clamp-2">
                  <Sparkles className="w-2.5 h-2.5 inline mr-1" /> {obj.context_summary}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-[#14101c] border border-white/10 rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="cookbook-edit-modal">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-base font-bold text-white">{editing === "new" ? "Nouvelle objection" : "Modifier"}</h3>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <Field label="Libellé court (titre)" value={form.label} onChange={(v) => setForm((p) => ({ ...p, label: v }))} testid="cookbook-form-label" />
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">Intent</label>
                <select
                  value={form.objection_intent}
                  onChange={(e) => setForm((p) => ({ ...p, objection_intent: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                  data-testid="cookbook-form-intent"
                >
                  <option value="objection">Objection</option>
                  <option value="risk">Risque</option>
                  <option value="question">Question</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <Textarea label="Réponse en français (pour l'agent)" value={form.agent_native} onChange={(v) => setForm((p) => ({ ...p, agent_native: v }))} testid="cookbook-form-native" />
              <Textarea label="Réponse traduite (à dire au contact)" value={form.agent_translated} onChange={(v) => setForm((p) => ({ ...p, agent_translated: v }))} testid="cookbook-form-translated" />
            </div>
            <div className="p-4 border-t border-white/5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)} data-testid="cookbook-form-cancel">Annuler</Button>
              <Button onClick={save} className="bg-purple-500 hover:bg-purple-400 text-white" data-testid="cookbook-form-save">Enregistrer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-sm text-white"
        data-testid={testid}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-sm text-white"
        data-testid={testid}
      />
    </div>
  );
}
